import { ChangeEvent, FC, useState, useEffect } from 'react';
import cryptoRandomString from 'crypto-random-string';
// Hooks
import { useAppSelector } from 'hooks/useStore';
// Models
import IQuiz from 'models/Quiz';
// Types
import QuizTypes from 'types/QuizTypes';
// Selectors
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectPreviewPageMode, selectTotalScore } from 'store/player/playerSelectors';
import { selectResultId, selectUserId } from 'store/auth/authSelectors';
// Components
import { Input } from 'components/Controls';
import { Button } from 'components/Buttons';
// i18next
import { Trans, useTranslation } from 'react-i18next';
// Utilities
import { textFromNormalToCamelCase } from 'utilities/Utilities';

interface IFormData {
  username: string;
  customFields?: Record<string, string>;
};

type Props = {
  onAsyncRegisterUser: (data:any, doRedirect?:boolean) => void;
  loading: boolean;
}

const maxNameLength = 25;

const AnonymousForm:FC<Props> = ({
  // Props
  onAsyncRegisterUser, loading
}) => {
  const { t } = useTranslation();
  // State
  const quiz:IQuiz = useAppSelector(selectQuiz);
  const totalScore:number = useAppSelector(selectTotalScore);
  const userId = useAppSelector(selectUserId);
  const resultId = useAppSelector(selectResultId);
  const previewPageMode = useAppSelector(selectPreviewPageMode);

  const [ rendered, setRendered ] = useState(false);
  const [ isSubmitted, setIsSubmitted ] = useState(false);
  const [ formData, setFormData ] = useState<IFormData>({
    username: '',
    customFields: {}
  });
  const [ errors, setErrors ] = useState({
    username: ''
  });

  useEffect(() => {
    if ( !quiz.registrationPage ){
      const randomString = cryptoRandomString({ length: 11 });
      const registerData = {
        name: 'anonymous',
        surname: 'user',
        email: `${randomString}@anonymous`,
        username: randomString,
        password: randomString
      };
      if ( !previewPageMode ) onAsyncRegisterUser({ registerData });
    } else {
      setRendered(true);
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const hasErrors = Object.values(errors).some(v => v);
    if ( isSubmitted && !hasErrors ){
      const randomString = cryptoRandomString({ length: 11 });
      const registerData:any = {
        name: 'anonymous',
        surname: 'user',
        email: `${randomString}@anonymous`,
        username: formData.username,
        password: randomString,
        customFields: {}
      };
      if (formData.customFields) {
        Object.keys(formData.customFields).forEach(key => {
          const camelCasedKey = textFromNormalToCamelCase(key);
          registerData.customFields[camelCasedKey] = formData.customFields?.[key];
        });
      }

      if (userId) {
        onAsyncRegisterUser({});
      } else {
        onAsyncRegisterUser({ registerData });
      }
      
    }
    // eslint-disable-next-line
  }, [isSubmitted, errors]);

  const handleChangeInput = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevState:any) => ({
      ...prevState,
      [name]: value
    }));
  }

  const handleClick = () => {
    setIsSubmitted(true);
    setErrors((prevState:any) => {
      const isRequired = !formData.username.trim();
      const isTooLong = formData.username.length > maxNameLength;
      return ({
        ...prevState,
        username: isRequired ? t('validations.requiredField') : isTooLong ? t('validations.maxLength', { max: maxNameLength }) : ''
      })
    });
  }

  const handleChangeCustomFields = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      customFields: {
        ...prevState.customFields,
        [name]: value
      }
    }));
  }

  useEffect(() => {
    if (!!userId && !!resultId) {
      setIsSubmitted(true);
    }
    // eslint-disable-next-line
  }, [resultId, userId]);

  const isScoringQuizzes = [QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type);
  const isPollOrPersonalityQuiz = [QuizTypes.Poll, QuizTypes.Personality, QuizTypes.RankingPoll].includes(quiz.type);
  const isSurveyQuiz = quiz.type === QuizTypes.Survey;

  if (userId || !rendered) return null;
  return (
    <div className="px-4">
      <h2 className="font-bold text-center text-primary text-xl mb-4">
        <Trans
          t={t}
          components={{ b: <b /> }}
          tOptions={{ score: totalScore }}
          i18nKey={isPollOrPersonalityQuiz
            ? 'pages.register.anonymousTitlePoll'
            : isSurveyQuiz
              ? 'pages.register.anonymousTitleSurvey'
              : quiz.registrationPage?.showPriorQuiz
                ? 'pages.register.anonymousTitlePriorQuiz'
                : 'pages.register.anonymousTitle'
          }
        />
      </h2>
      {quiz.spotPrizesEnabled && quiz.spotPrizes && quiz.spotPrizes.length && isScoringQuizzes ? (
        <span className="block text-center font-bold mb-4 text-primary">{t('pages.register.prizeHint', { prize: quiz.spotPrizes[0] })}</span>
      ) : null}
      <Input
        className="bg-[#f2f2f2]"
        name="username" id="username" placeholder={t('labels.name')}
        value={formData.username}
        onChange={handleChangeInput}
        error={Boolean(errors.username)}
        errorText={errors.username}
      />
      {quiz.registrationPage?.customFields?.map((field:string) => (
        <Input
          key={field} name={field} id={field} placeholder={field}
          className="bg-[#f2f2f2]"
          value={formData.customFields?.[textFromNormalToCamelCase(field)] as string}
          onChange={handleChangeCustomFields}
        />
      ))}
      <div className="mb-4">
        <Button
          className="
            w-full flex items-center justify-center gap-3
            text-2xl uppercase primary-button
          "
          onClick={handleClick}
          loading={loading}
        >
          {t('buttons.register')}
        </Button>
      </div>
    </div>
  )
}

export default AnonymousForm;
